import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _dec, _dec2, _dec3, _dec4, _class, _descriptor;
var _excluded = ["innerRef"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import cx from 'classnames';
import { task, TaskGroup } from 'mobx-task';
import { observer, useAsObservableSource } from 'mobx-react';
import styles from './SuggestedDocuments.m.sass';
import FileButton from '../../../components/FileButton';
import CloudIcon from './cloud.i.svg';
import TickmarkIcon from './tickmark.i.svg';
import CloseIcon from './close.i.svg';
import DocumentPicker from '../DocumentPicker';
import { action, observable, computed } from 'mobx';
import ProgressSpinner from '../../../components/ProgressSpinner';
import { useDropzone } from '../../../hooks/use-dropzone';
import { extractMessageFromError } from '../../../utils/errorUtil';

/**
 * @typedef {import('../../Document').default} Document
 * @typedef {import('../../DocumentStore').default} DocumentStore
 */

/**
 * @typedef {Object} Props
 * @property {SuggestedDocument[]} value
 * @property {FetchDocument} fetchDocument
 * @property {DocumentStore} documentStore
 * @property {UploadDocument} uploadDocument
 * @property {AsyncDocumentOperation} linkDocument
 * @property {AsyncDocumentOperation} unlinkDocument
 * @property {DocumentFilterPredicate | undefined} predicate
 * @property {DocumentsGetter | undefined} getDocuments
 * @property {InfiniteScrollProvider | undefined} scrollProvider
 * @property {'left' | 'right} documentPickerPosition
 */

/**
 * @typedef {Object} ItemProps
 * @property {SuggestedDocument[]} items
 * @property {SuggestedDocument} item
 * @property {FetchDocument} fetchDocument
 * @property {DocumentStore} documentStore
 * @property {InfiniteScrollProvider} scrollProvider
 * @property {UploadDocument} uploadDocument
 * @property {AsyncDocumentOperation} linkDocument
 * @property {DocumentsGetter | undefined} getDocuments
 * @property {AsyncDocumentOperation} unlinkDocument
 * @property {'left' | 'right} documentPickerPosition
 */

/**
 * @typedef {Object} SuggestedDocument
 * @property {string} text - the text to render as the suggestion
 * @property {string} id - ID of the suggestion. Used when calling `onChange`
 * @property {string} tagIds - Tag IDs to assign to documents by this suggestion
 * @property {string| undefined} documentId - ID of the current document assigned to this suggestion
 */

/**
 * @callback UploadDocument
 * @param {SuggestedDocument} item
 * @param {File} file
 * @param {(doc: Document) => void} receiveDocument
 * @returns {Promise<unknown>}
 */

/**
 * @callback AsyncDocumentOperation
 * @param {SuggestedDocument} item
 * @param {Document} document
 * @returns {Promise<unknown>}
 */

/**
 * @callback DocumentsGetter
 * @returns {Document[]}
 */

/**
 * @callback DocumentFilterPredicate
 * @param {Document} document
 * @returns {boolean}
 */

/**
 * @callback FetchDocument
 * @param {string} documentId
 * @returns {Promise<Document>}
 */

/**
 * Suggested documents.
 *
 * @param {Props} props
 */
var SuggestedDocuments = observer(
/**
 * @param {Props} props
 */
function (_ref) {
  var fetchDocument = _ref.fetchDocument,
    uploadDocument = _ref.uploadDocument,
    linkDocument = _ref.linkDocument,
    documentStore = _ref.documentStore,
    unlinkDocument = _ref.unlinkDocument,
    documentPickerPosition = _ref.documentPickerPosition,
    scrollProvider = _ref.scrollProvider,
    getDocuments = _ref.getDocuments,
    _ref$predicate = _ref.predicate,
    predicate = _ref$predicate === void 0 ? function () {
      return true;
    } : _ref$predicate,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? [] : _ref$value,
    _ref$uploadIcon = _ref.uploadIcon,
    uploadIcon = _ref$uploadIcon === void 0 ? null : _ref$uploadIcon,
    _ref$finishedIcon = _ref.finishedIcon,
    finishedIcon = _ref$finishedIcon === void 0 ? null : _ref$finishedIcon,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$linkingEnabled = _ref.linkingEnabled,
    linkingEnabled = _ref$linkingEnabled === void 0 ? true : _ref$linkingEnabled;
  return /*#__PURE__*/React.createElement("div", {
    className: cx(styles.root)
  }, value.map(function (v) {
    return /*#__PURE__*/React.createElement(Item, {
      key: v.id,
      item: v,
      items: value,
      documentStore: documentStore,
      uploadDocument: uploadDocument,
      linkDocument: linkDocument,
      unlinkDocument: unlinkDocument,
      getDocuments: getDocuments,
      documentPickerPosition: documentPickerPosition,
      scrollProvider: scrollProvider !== null && scrollProvider !== void 0 ? scrollProvider : documentStore.userDocumentsScrollProvider,
      fetchDocument: fetchDocument,
      predicate: predicate,
      uploadIcon: uploadIcon,
      finishedIcon: finishedIcon,
      readOnly: readOnly,
      linkingEnabled: linkingEnabled
    });
  }));
});
export default SuggestedDocuments;
var Item = observer(
/**
 * Document item.
 *
 * @param {ItemProps} props
 */
function Item(props) {
  var _props$uploadIcon, _props$finishedIcon;
  var observableProps = useAsObservableSource(props);
  // Creates a view model once.
  var _React$useState = React.useState(function () {
      return new SuggestedDocumentViewModel(observableProps);
    }),
    _React$useState2 = _slicedToArray(_React$useState, 1),
    viewModel = _React$useState2[0];
  // Tries to fetch the document when the item changes.
  React.useEffect(function () {
    viewModel.activate();
  }, [props.item.id]);
  var doc = viewModel.document;
  var operationPending = viewModel.taskGroup.pending;
  var uploadProgress = doc && doc.uploadProgress ||
  // Simulate upload progress while waiting to receive the document.
  viewModel.uploadDocument.pending && {
    percent: 0
  };
  var finished = !!(doc && !uploadProgress);
  var filesHandler = function filesHandler(files) {
    return files.length > 0 && viewModel.uploadDocument(files[0]);
  };
  var _useDropzone = useDropzone(!doc && !viewModel.uploadDocument.pending && filesHandler),
    _useDropzone2 = _slicedToArray(_useDropzone, 2),
    dragState = _useDropzone2[0],
    dragRef = _useDropzone2[1];
  var docDisplayText = function () {
    var versionFile = doc && doc.latestVersion && doc.latestVersion.files[0];
    return versionFile && versionFile.fileName;
  }();
  var UploadIcon = (_props$uploadIcon = props.uploadIcon) !== null && _props$uploadIcon !== void 0 ? _props$uploadIcon : CloudIcon;
  var FinishedIcon = (_props$finishedIcon = props.finishedIcon) !== null && _props$finishedIcon !== void 0 ? _props$finishedIcon : TickmarkIcon;
  return /*#__PURE__*/React.createElement("div", {
    ref: dragRef,
    className: cx(finished ? styles.filled : styles.vacant, dragState.isOver && styles.dragOver, viewModel.errorMessage && styles.errored)
  }, /*#__PURE__*/React.createElement("div", {
    className: finished ? styles.tickmarkIcon : styles.cloudIcon
  }, viewModel.activate.pending ? /*#__PURE__*/React.createElement("div", {
    className: styles.iconBoundingBox
  }, /*#__PURE__*/React.createElement(ProgressSpinner, {
    blue: true,
    size: 24,
    thickness: 2
  })) : finished ? /*#__PURE__*/React.createElement(FinishedIcon, null) : /*#__PURE__*/React.createElement(UploadIcon, null)), /*#__PURE__*/React.createElement("div", {
    className: styles.info
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.text
  }, props.item.text), uploadProgress ? /*#__PURE__*/React.createElement("div", {
    className: styles.outer
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: "".concat(uploadProgress.percent, "%")
    },
    className: styles.bar
  })) : /*#__PURE__*/React.createElement("p", {
    className: cx(styles.subText, viewModel.errorMessage && styles.errorText)
  }, viewModel.errorMessage ? /*#__PURE__*/React.createElement(React.Fragment, null, viewModel.errorMessage) : !doc ? viewModel.activate.pending ? /*#__PURE__*/React.createElement(React.Fragment, null, "Fetching saved document information...") : dragState.isOver ? /*#__PURE__*/React.createElement(React.Fragment, null, "Drop your file to upload") : !props.readOnly && /*#__PURE__*/React.createElement(React.Fragment, null, "Drag and drop your file here") : /*#__PURE__*/React.createElement(React.Fragment, null, docDisplayText))),
  // Don't render any actions if we are currently uploading or
  // fetching the document. Render a spinner when a link operation is running,
  // otherwise render the actions.
  uploadProgress || viewModel.activate.pending ? null : operationPending ? /*#__PURE__*/React.createElement(ProgressSpinner, {
    blue: true,
    size: 24,
    thickness: 2
  }) : /*#__PURE__*/React.createElement("div", {
    className: styles.actions
  }, doc && !props.readOnly ? /*#__PURE__*/React.createElement("a", {
    className: styles.clearButton,
    onClick: viewModel.unlinkDocument,
    "data-test": "document-clear-button-".concat(props.item.text)
  }, /*#__PURE__*/React.createElement(CloseIcon, null)) : /*#__PURE__*/React.createElement(React.Fragment, null, !props.readOnly && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FileButton, {
    multiple: false,
    onChange: filesHandler,
    "data-test": "document-upload-button-".concat(props.item.text)
  }, /*#__PURE__*/React.createElement("a", {
    className: styles.action
  }, "Upload File"))), /*#__PURE__*/React.createElement(DocumentPicker, {
    position: props.documentPickerPosition,
    documentStore: props.documentStore,
    emptyText: "If you uploaded documents to other jobs (or in the All Documents section) you can link them to this job.",
    searchPlaceholder: "Search for existing documents to link to this job...",
    disabled: viewModel.disabled,
    closeOnSelect: true,
    scrollProvider: props.scrollProvider,
    getDocuments: props.getDocuments
    // Filters out docs already linked.
    ,
    filter: function filter(doc) {
      return props.items.some(function (v) {
        return v.documentId === doc.id;
      }) === false && props.predicate(doc);
    },
    onDocumentSelect: viewModel.linkDocument,
    button: function button(_ref2) {
      var innerRef = _ref2.innerRef,
        buttonProps = _objectWithoutProperties(_ref2, _excluded);
      return !props.readOnly && props.linkingEnabled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: styles.actionSeparator
      }, "|"), /*#__PURE__*/React.createElement("a", _extends({
        className: styles.action
      }, buttonProps, {
        ref: innerRef,
        "data-test": "document-link-button-".concat(props.item.text)
      }), "Link")) : doc && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: styles.actionSeparator
      }, "|"), /*#__PURE__*/React.createElement("a", {
        className: styles.action,
        onClick: function onClick(e) {
          e.preventDefault();
          e.stopPropagation();
          props.documentStore.download(doc, false);
        },
        "data-test": "document-download-button-".concat(props.item.text)
      }, "Download"));
    }
  }))));
});

/**
 * View model for the suggested document.
 */
var SuggestedDocumentViewModel = (_dec = action.bound, _dec2 = task.resolved, _dec3 = task.resolved, _dec4 = task.resolved, _class = /*#__PURE__*/function () {
  /**
   * Creates the view model.
   *
   * @param {ItemProps} props the props for the item.
   * IMPORTANT: Don't dereference in the constructor, it won't work because the props
   * are observable and will change on rerender.
   */
  function SuggestedDocumentViewModel(props) {
    _classCallCheck(this, SuggestedDocumentViewModel);
    /**
     * @type {Document | null}
     */
    _initializerDefineProperty(this, "document", _descriptor, this);
    this.props = props;
    this.uploadDocument = this.uploadDocument.bind(this);
    this.linkDocument = this.linkDocument.bind(this);
    this.activate = this.activate.bind(this);
    this.unlinkDocument = this.unlinkDocument.bind(this);
    this.taskGroup = TaskGroup([this.uploadDocument, this.linkDocument, this.unlinkDocument, this.activate]);
  }

  /**
   * Error message, if any.
   */
  return _createClass(SuggestedDocumentViewModel, [{
    key: "errorMessage",
    get: function get() {
      return this.taskGroup.rejected ? extractMessageFromError(this.taskGroup.error) : null;
    }

    /**
     * Fetches the document
     *
     * @returns {Promise<unknown>}
     */
  }, {
    key: "activate",
    value: (function () {
      var _activate = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var doc;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (this.props.item.documentId) {
                _context.next = 3;
                break;
              }
              this.setDocument(null);
              return _context.abrupt("return");
            case 3:
              if (!this.document) {
                _context.next = 5;
                break;
              }
              return _context.abrupt("return");
            case 5:
              _context.next = 7;
              return this.props.fetchDocument(this.props.item.documentId);
            case 7:
              doc = _context.sent;
              if (!(doc && doc.archived)) {
                _context.next = 11;
                break;
              }
              this.setDocument(null);
              return _context.abrupt("return");
            case 11:
              this.setDocument(doc);
            case 12:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function activate() {
        return _activate.apply(this, arguments);
      }
      return activate;
    }()
    /**
     * Sets the document.
     *
     * @param {Document | null} doc
     */
    )
  }, {
    key: "setDocument",
    value: function setDocument(doc) {
      this.document = doc;
    }

    /**
     * Uploads the document.
     *
     * @param {File} file
     */
  }, {
    key: "uploadDocument",
    value: (function () {
      var _uploadDocument = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(file) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.prev = 0;
              _context2.next = 3;
              return this.props.uploadDocument(this.props.item, file, this.setDocument);
            case 3:
              _context2.next = 9;
              break;
            case 5:
              _context2.prev = 5;
              _context2.t0 = _context2["catch"](0);
              this.setDocument(null);
              throw _context2.t0;
            case 9:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this, [[0, 5]]);
      }));
      function uploadDocument(_x) {
        return _uploadDocument.apply(this, arguments);
      }
      return uploadDocument;
    }()
    /**
     * Links a document.
     *
     * @param {Document} doc
     */
    )
  }, {
    key: "linkDocument",
    value: (function () {
      var _linkDocument = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(doc) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return this.props.linkDocument(this.props.item, doc);
            case 2:
              this.setDocument(doc);
            case 3:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this);
      }));
      function linkDocument(_x2) {
        return _linkDocument.apply(this, arguments);
      }
      return linkDocument;
    }()
    /**
     * Unlinks a document.
     */
    )
  }, {
    key: "unlinkDocument",
    value: (function () {
      var _unlinkDocument = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              if (!this.document) {
                _context4.next = 3;
                break;
              }
              _context4.next = 3;
              return this.props.unlinkDocument(this.props.item, this.document);
            case 3:
              this.setDocument(null);
            case 4:
            case "end":
              return _context4.stop();
          }
        }, _callee4, this);
      }));
      function unlinkDocument() {
        return _unlinkDocument.apply(this, arguments);
      }
      return unlinkDocument;
    }())
  }]);
}(), _descriptor = _applyDecoratedDescriptor(_class.prototype, "document", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "errorMessage", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "errorMessage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "activate", [task], Object.getOwnPropertyDescriptor(_class.prototype, "activate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setDocument", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "setDocument"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "uploadDocument", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "uploadDocument"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "linkDocument", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "linkDocument"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unlinkDocument", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "unlinkDocument"), _class.prototype), _class);
//# sourceMappingURL=index.js.map
